import {
  Box,
  Checkbox,
  CheckboxGroup,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import useInterpolateTranslatedField from "@raiden/library/hooks/useInterpolateTranslatedField";
import { memo, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { IoSearchOutline } from "react-icons/io5";
import { MdTune } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";
import SWRHandler from "../../../components/SWR/SWRHandler";
import { useSearchInfiniteContext } from "../../SearchInfinite/useSearchInfiniteContext";

const Filters = memo(
  /**
   * @typedef {Object} Props
   * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]>>} swrResponsePostCategories
   *
   * @param {Props} props
   */
  function Filters({ swrResponsePostCategories }) {
    const intl = useIntl();

    const [areFiltersVisible, setAreFiltersVisible] = useState(false);

    const translate = useInterpolateTranslatedField();

    const { appliedFilters, reset } = useSearchInfiniteContext();

    const handleClickTune = useCallback(() => {
      setAreFiltersVisible((areFiltersVisible) => !areFiltersVisible);
    }, []);

    /** @type {import("react-hook-form").UseFormReturn<import("./Handler").NewsSearchFormValues>} */
    const { control } = useFormContext();

    return (
      <Stack
        backgroundColor={{ base: "gray.50", lg: undefined }}
        spacing="0.375rem"
        p="1rem">
        <Box>
          <Flex>
            <FormController
              control={control}
              name="term"
              flexGrow={1}
              render={(field) => (
                <InputGroup>
                  <Input
                    type="search"
                    placeholder={intl.formatMessage({
                      defaultMessage: "Rechercher...",
                    })}
                    height="44px"
                    backgroundColor="#fff"
                    boxShadow="0 1px 0 0 #E2E8F0"
                    _hover={{
                      backgroundColor: "#fff",
                    }}
                    {...field}
                  />

                  <InputRightElement>
                    <Icon as={IoSearchOutline} />
                  </InputRightElement>
                </InputGroup>
              )}
            />

            <Box display={{ base: "block", lg: "none" }}>
              <IconButton
                onClick={handleClickTune}
                icon={<Icon as={MdTune} />}
                position="relative"
                ml="0.75rem"
                variant="outline"
                borderRadius="full"
                w="44px"
                h="44px"
                colorScheme="brandSecondary"
                color={areFiltersVisible ? "#fff" : "brandSecondary.500"}
                backgroundColor={
                  areFiltersVisible ? "brandSecondary.500" : "#fff"
                }
                borderColor="brandSecondary.500"
                _focus={{
                  borderColor: "brandSecondary.500",
                  backgroundColor: areFiltersVisible
                    ? "var(--chakra-colors-brandSecondary-500) !important"
                    : "#fff !important",
                }}
                aria-label={intl.formatMessage({ defaultMessage: "Filtrer" })}
                _after={
                  appliedFilters.filter((filter) => filter !== "term").length >
                  0
                    ? {
                        content: `"${
                          appliedFilters.filter((filter) => filter !== "term")
                            .length
                        }"`,
                        position: "absolute",
                        top: "0",
                        right: "0",
                        backgroundColor: "brandPrimary.500",
                        color: "#fff",
                        px: ".5rem",
                        borderRadius: "full",
                        fontsize: "16px",
                        fontWeight: 400,
                        lineHeight: "1.5rem",
                        transform: "translate(50%, -50%)",
                      }
                    : undefined
                }
              />
            </Box>
          </Flex>

          <Flex
            justify="flex-end"
            pt=".25rem"
            opacity={appliedFilters.length > 0 ? 1 : 0}
            visibility={appliedFilters.length > 0 ? "visible" : "hidden"}
            transition="all .25s ease">
            <Link
              onClick={reset}
              textAlign="right"
              fontSize="0.75rem"
              color="gray.600">
              <FormattedMessage defaultMessage="Réinitialiser les filtres" />
            </Link>
          </Flex>
        </Box>

        <SWRHandler
          swrResponse={swrResponsePostCategories}
          renderNoResultSplash={false}
          keepDataOnRevalidation>
          {({ data: postCategories }) => (
            <Stack
              display={{
                base: areFiltersVisible ? "block" : "none",
                lg: "block",
              }}
              spacing="1rem">
              <Text
                fontSize="1.25rem"
                fontWeight={400}
                lineHeight="1.5rem"
                color="gray.500">
                <FormattedMessage defaultMessage="Catégories" />
              </Text>

              <Box>
                <FormController
                  control={control}
                  name="category_id"
                  render={(field) => (
                    <Box ref={field.ref}>
                      <CheckboxGroup {...{ ...field, ref: undefined }}>
                        <Stack spacing="0.75rem">
                          {postCategories.map((category) => (
                            <Checkbox
                              value={String(category.id)}
                              colorScheme="brandPrimary"
                              key={category.id}>
                              {translate(category.name)}
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                    </Box>
                  )}
                />
              </Box>
            </Stack>
          )}
        </SWRHandler>
      </Stack>
    );
  },
);

export default Filters;
